import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import useAuthStore from '../../store/authStore';
import api from '../../config/api';
import { useFeatures } from '../../hooks/useFeatures';
import useAddons from '../../hooks/useAddons';
import useViewOnly from '../../hooks/useViewOnly';

const GREEN = '#2e9f86';

/* ─── Fullscreen toggle button ───────────────────────────────────────────── */
function FullscreenButton({ expanded }) {
  const [isFS, setIsFS] = useState(!!document.fullscreenElement);
  useEffect(() => {
    const handler = () => setIsFS(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);
  const toggle = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.();
  };
  const icon = isFS
    ? <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5M15 15l5.25 5.25" /></svg>
    : <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" /></svg>;

  if (expanded) {
    return (
      <button onClick={toggle} title={isFS ? 'Exit Fullscreen' : 'Fullscreen'}
        className="flex items-center gap-3 w-full rounded-lg hover:bg-[#e2e2e2] text-black transition-colors px-3 py-3">
        {icon}
        <span className="truncate text-base">{isFS ? 'Exit Fullscreen' : 'Fullscreen'}</span>
      </button>
    );
  }
  return (
    <button onClick={toggle} title={isFS ? 'Exit Fullscreen' : 'Fullscreen'}
      className="p-3 rounded-lg text-black hover:bg-[#e2e2e2] transition-colors">
      {icon}
    </button>
  );
}

/* ─── role permissions ──────────────────────────────────────────────────── */
const POS_ROLES = ['admin', 'manager', 'cashier'];

/* ─── icons ─────────────────────────────────────────────────────────────── */
const MenuIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
  </svg>
);
const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const DashboardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
  </svg>
);
const AccountIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  </svg>
);
const ProductIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
  </svg>
);
const DocumentIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);
const StockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
  </svg>
);
const CashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
  </svg>
);
const ReportIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
  </svg>
);
const SettingsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.43l-1.003.767c-.3.23-.452.617-.432.998.003.074.005.147.005.22 0 .072-.002.146-.005.22a1.1 1.1 0 01-.432.998l1.003.767a1.125 1.125 0 01.26 1.43l-1.296 2.247a1.125 1.125 0 01-1.37.49l-1.216-.456a1.125 1.125 0 00-1.07.124c-.073.044-.146.087-.22.128-.332.183-.582.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281a1.125 1.125 0 00-.644-.87c-.074-.04-.147-.083-.22-.127a1.125 1.125 0 00-1.075-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.43l1.003-.767c.301-.23.452-.617.432-.998-.003-.074-.005-.147-.005-.22 0-.072.002-.146.005-.22a1.1 1.1 0 01-.432-.998l-1.003-.767a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.49l1.216.456c.356.133.751.072 1.076-.124.072-.041.146-.084.218-.128.332-.183.582-.495.645-.869L9.594 3.94z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);
const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.2} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);
const GlobeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18zm0 0a8.949 8.949 0 004.951-1.488M12 21a8.949 8.949 0 01-4.951-1.488M3.055 11h17.89M12 3c2.5 2.5 3.5 5.5 3.5 9s-1 6.5-3.5 9c-2.5-2.5-3.5-5.5-3.5-9s1-6.5 3.5-9z" />
  </svg>
);
const ExpenseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 14.25l6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0c1.1.128 1.907 1.077 1.907 2.185zM9.75 9h.008v.008H9.75V9zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 4.5h.008v.008h-.008V13.5zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
  </svg>
);
const DeliveryIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
  </svg>
);
const AddOnsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const HealthIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
  </svg>
);
const ChatIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM2.25 12.76c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
  </svg>
);

/* ─── role-filtered menu builder (shared) ────────────────────────────────── */
function useMenuItems() {
  const { t } = useTranslation();
  const features = useFeatures();
  const addons = useAddons();
  const viewOnly = useViewOnly();   // MMM Fashion shop: sells on its computer, the web is for reports
  const { user } = useAuthStore();
  const role = user?.role || 'viewer';

  const all = [
    { path: '/',          label: t('dashboard', 'Dashboard'),         icon: <DashboardIcon />, roles: ['admin', 'manager', 'cashier', 'viewer'] },
    { path: '/documents', label: t('invoices', 'Invoices / فواتير'),  icon: <DocumentIcon />,  roles: ['admin', 'manager', 'cashier', 'viewer'] },
    { path: '/products',  label: t('products', 'Products'),           icon: <ProductIcon />,   roles: ['admin', 'manager', 'cashier', 'viewer'] },
    { path: '/reports',   label: t('reports', 'Reports'),             icon: <ReportIcon />,    roles: ['admin', 'manager'] },
    { path: '/accounts',  label: t('accounts', 'Accounts'),           icon: <AccountIcon />,   roles: ['admin', 'manager', 'cashier', 'viewer'] },
    { path: '/stock',     label: t('stock', 'Stock'),                 icon: <StockIcon />,     roles: ['admin', 'manager'] },
    { path: '/cashflow',  label: t('cashflow', 'Cash Flow'),          icon: <CashIcon />,      roles: ['admin', 'manager'] },
    { path: '/expenses',  label: t('expenses', 'Expenses'),           icon: <ExpenseIcon />,   roles: ['admin', 'manager'] },
    { path: '/finance',   label: t('finance', 'Finance'),             icon: <CashIcon />,      roles: ['admin', 'manager'] },
    { path: '/hr',        label: t('employeesPayroll', 'Employees & payroll'), icon: <AccountIcon />, roles: ['admin', 'manager', 'accountant'] },
    { path: '/my-work',   label: t('myWork', 'My work'),              icon: <DashboardIcon />, roles: ['admin', 'manager', 'accountant', 'cashier', 'viewer', 'waiter', 'kitchen', 'driver'] },
    ...(features.accounting ? [{ path: '/accounting', label: t('accounting', 'Accounting'), icon: <ReportIcon />, roles: ['admin'] }] : []),
    ...(features.delivery || role === 'driver'
      ? [{ path: '/delivery', label: t('delivery', 'Delivery'), icon: <DeliveryIcon />, roles: ['admin', 'manager', 'cashier', 'driver'] }]
      : []),
    ...(addons.ai_chat ? [{ path: '/assistant', label: t('aiAssistant', 'AI Assistant'), icon: <ChatIcon />, roles: ['admin', 'manager', 'cashier', 'viewer'] }] : []),
    ...(addons.ai_analysis ? [{ path: '/health-check', label: t('healthCheck', 'Health Check'), icon: <HealthIcon />, roles: ['admin'] }] : []),
    { path: '/settings',  label: t('settings', 'Settings'),           icon: <SettingsIcon />,  roles: ['admin'] },
  ];

  return {
    items: all.filter((i) => i.roles.includes(role)),
    showPos: POS_ROLES.includes(role) && !viewOnly,
    role,
  };
}

/* ─── MMM POS wordmark ────────────────────────────────────────────────────── */
function Logo() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
      <div style={{
        width: 38, height: 38, borderRadius: 11, flexShrink: 0,
        background: `linear-gradient(135deg, ${GREEN} 0%, #1f7d68 100%)`,
        boxShadow: '0 4px 12px rgba(46,159,134,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 18 }}>
          <span style={{ width: 4, height: 9,  background: 'rgba(255,255,255,0.9)', borderRadius: 2 }} />
          <span style={{ width: 4, height: 14, background: '#fff', borderRadius: 2 }} />
          <span style={{ width: 4, height: 18, background: 'rgba(255,255,255,0.9)', borderRadius: 2 }} />
        </div>
      </div>
      <div style={{ lineHeight: 1 }}>
        <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: -0.3, color: '#0d0d0d' }}>
          MMM<span style={{ color: GREEN }}>POS</span>
        </div>
        <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: 1, color: '#9ca3af', marginTop: 3 }}>
          developed by Kassem Abboud
        </div>
      </div>
    </div>
  );
}

/* ─── Sidebar inner content (shared between desktop + mobile drawer) ── */
function SidebarContent({ onNavigate, onCollapse }) {
  const { t, i18n } = useTranslation();
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const { items: menuItems, showPos } = useMenuItems();

  const { data: sub } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.get('/api/subscription').then((r) => r.data),
    refetchInterval: 60000,
  });

  const isAr = (i18n.language || 'en').startsWith('ar');

  const toggleLang = () => {
    const next = isAr ? 'en' : 'ar';
    i18n.changeLanguage(next);
    document.documentElement.dir = next === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = next;
  };

  const subLabel = sub
    ? (sub.status === 'active'
        ? `${t('subscription', 'Subscription')}: ${sub.days_left} ${t('daysLeft', 'days left')}`
        : `${t('trial', 'Trial')}: ${sub.days_left} ${t('daysLeft', 'days left')}`)
    : '';
  const subDanger = sub && sub.days_left <= 3;

  const handleLogout = () => { logout(); navigate('/login'); onNavigate?.(); };

  const handleNav = (path) => {
    navigate(path);
    onNavigate?.();
  };

  return (
    <div className="flex flex-col bg-[#f9f9f9]" style={{ height: '100%', overflow: 'hidden' }}>
      {/* top scrollable area */}
      <div className="flex flex-col min-h-0 flex-1" style={{ overflow: 'hidden' }}>

        {/* lang + logo row */}
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <Logo />
          <div className="flex items-center gap-2">
            <button
              onClick={toggleLang}
              title={isAr ? 'Switch to English' : 'التبديل إلى العربية'}
              className="flex items-center justify-center gap-1.5 rounded-full border px-3 h-9 text-[13px] font-semibold transition-colors"
              style={{ borderColor: GREEN, color: GREEN, background: '#eafaf6' }}
            >
              <GlobeIcon />
              <span>{isAr ? 'EN' : 'ع'}</span>
            </button>
            {onCollapse && (
              <button
                onClick={onCollapse}
                className="p-2 rounded-lg hover:bg-[#e2e2e2] text-gray-400 transition-colors"
                title="Collapse sidebar"
              >
                <CloseIcon />
              </button>
            )}
          </div>
        </div>

        {/* subscription badge */}
        {sub && (
          <div
            style={{
              margin: '0 12px 10px', padding: '8px 12px', borderRadius: 10,
              background: subDanger ? '#fef2f2' : '#f0fdf8',
              border: `1.5px solid ${subDanger ? '#fecaca' : '#bbf7d0'}`,
              color: subDanger ? '#b91c1c' : '#065f46',
              fontSize: 12, fontWeight: 600, textAlign: 'center',
            }}
          >
            {subLabel}
          </div>
        )}

        {/* New POS button — admin/manager/cashier only */}
        {showPos && (
          <div className="px-3 mb-4 flex-shrink-0">
            <button
              onClick={() => handleNav('/pos')}
              className="flex items-center gap-3 w-full rounded-lg text-base text-white font-medium cursor-pointer shadow-sm hover:opacity-90 px-3 py-3"
              style={{ background: GREEN }}
            >
              <PlusIcon />
              <span className="truncate tracking-wide">{t('newPos', 'New POS')}</span>
            </button>
          </div>
        )}

        {/* nav */}
        <nav className="flex-1 px-2 space-y-1 pb-4" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              onClick={() => onNavigate?.()}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg text-base font-normal transition-colors hover:bg-[#e2e2e2] text-black tracking-wide px-3 py-3
                ${isActive ? 'bg-[#e5e5e5] border border-[#d9d9d9] font-medium shadow-sm' : ''}`
              }
            >
              <div className="flex-shrink-0 text-black">{item.icon}</div>
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>

      {/* logout + fullscreen */}
      <div className="p-2 border-t border-[#e5e5e5] bg-[#f4f4f4] flex-shrink-0 flex flex-col gap-1">
        <FullscreenButton expanded />
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full rounded-lg hover:bg-[#e2e2e2] text-black hover:text-red-600 transition-colors px-3 py-3"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 flex-shrink-0">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
          </svg>
          <span className="truncate text-base">{t('logout', 'Log out')}</span>
        </button>
      </div>
    </div>
  );
}

/* ─── Main Sidebar export ─────────────────────────────────────────────────── */
export default function Sidebar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();

  // close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      {/* ── MOBILE: top bar with hamburger ── */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3 bg-white border-b border-[#e5e5e5]"
           style={{ height: 56 }}>
        <button onClick={() => setMobileOpen(true)} className="p-2 rounded-lg hover:bg-gray-100 text-black">
          <MenuIcon />
        </button>
        {/* center logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: `linear-gradient(135deg, ${GREEN} 0%, #1f7d68 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 14 }}>
              <span style={{ width: 3, height: 7,  background: 'rgba(255,255,255,0.9)', borderRadius: 1 }} />
              <span style={{ width: 3, height: 10, background: '#fff', borderRadius: 1 }} />
              <span style={{ width: 3, height: 14, background: 'rgba(255,255,255,0.9)', borderRadius: 1 }} />
            </div>
          </div>
          <span style={{ fontSize: 16, fontWeight: 800, color: '#0d0d0d' }}>
            MMM<span style={{ color: GREEN }}>POS</span>
          </span>
        </div>
        {/* placeholder to center logo */}
        <div style={{ width: 36 }} />
      </div>

      {/* ── MOBILE: drawer overlay ── */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 flex"
          onClick={() => setMobileOpen(false)}
        >
          {/* backdrop */}
          <div className="absolute inset-0 bg-black/40" />
          {/* drawer */}
          <div
            className="relative w-72 max-w-[85vw] h-full shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* close button */}
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 z-10 p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            >
              <CloseIcon />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* ── DESKTOP: fixed sidebar ── */}
      <div
        className={`hidden md:flex flex-col h-screen bg-[#f9f9f9] border-r border-[#e5e5e5] select-none antialiased transition-all duration-300 flex-shrink-0
          ${isCollapsed ? 'w-16' : 'w-64'}`}
      >
        {isCollapsed ? (
          /* collapsed icon-only view */
          <CollapsedSidebar onExpand={() => setIsCollapsed(false)} />
        ) : (
          <div className="flex flex-col h-full">
            <SidebarContent onCollapse={() => setIsCollapsed(true)} />
          </div>
        )}
      </div>
    </>
  );
}

/* ─── Collapsed desktop sidebar (icon only) ──────────────────────────────── */
function CollapsedSidebar({ onExpand }) {
  const { t } = useTranslation();
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const { items: menuItems, showPos } = useMenuItems();

  return (
    <div className="flex flex-col h-full bg-[#f9f9f9] items-center py-3 gap-1">
      {/* expand button */}
      <button onClick={onExpand} className="p-2 hover:bg-[#e2e2e2] rounded-lg text-black mb-1">
        <MenuIcon />
      </button>
      {/* POS button */}
      {showPos && (
        <button
          onClick={() => navigate('/pos')}
          className="p-2.5 rounded-lg text-white hover:opacity-90 mb-2"
          style={{ background: GREEN }}
          title="New POS"
        >
          <PlusIcon />
        </button>
      )}
      {/* nav icons */}
      {menuItems.map(item => (
        <NavLink
          key={item.path}
          to={item.path}
          end={item.path === '/'}
          title={item.label}
          className={({ isActive }) =>
            `p-3 rounded-lg text-black hover:bg-[#e2e2e2] transition-colors
            ${isActive ? 'bg-[#e5e5e5] border border-[#d9d9d9] shadow-sm' : ''}`
          }
        >
          {item.icon}
        </NavLink>
      ))}
      {/* fullscreen + logout */}
      <div className="mt-auto p-2 border-t border-[#e5e5e5] w-full flex flex-col items-center gap-1">
        <FullscreenButton />
        <button
          onClick={() => { logout(); navigate('/login'); }}
          title={t('logout', 'Log out')}
          className="p-3 rounded-lg text-black hover:text-red-600 hover:bg-[#e2e2e2] transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
          </svg>
        </button>
      </div>
    </div>
  );
}